<template>
    <div class="login-container">
        <!-- Background Elements -->
        <div class="background-elements">
            <div class="bg-circle circle-1"></div>
            <div class="bg-circle circle-2"></div>
            <div class="bg-circle circle-3"></div>
        </div>

        <!-- Main Content -->
        <div class="container-fluid h-100">
            <div class="row h-100 align-items-center justify-content-center">
                <!-- Centered Reset Password Form -->
                <div class="col-md-4">
                    <div class="login-section">
                        <div class="login-card">
                            <div class="login-header">

                                <h3 class="login-title">Forgot Password?</h3>
                                <p class="login-subtitle">No problem. Just let us know your email address and we will email you a password reset link.</p>
                            </div>

                            <!-- Success Message -->
                            <div v-if="$page.props.flash?.success" class="alert alert-success mb-3">
                                <i class="bi bi-check-circle me-2"></i>
                                {{ $page.props.flash.success }}
                            </div>

                            <form class="login-form" @submit.prevent="handleSubmit">
                                <!-- Email Field -->
                                <div class="form-group mb-4">
                                    <label for="email" class="form-label">
                                        <i class="bi bi-envelope me-2"></i>Email Address
                                    </label>
                                    <input type="email" class="form-control" :class="{ 'is-invalid': form.errors.email }" id="email" v-model="form.email" placeholder="Enter your email" required autofocus>
                                    <div v-if="form.errors.email" class="invalid-feedback">
                                        {{ form.errors.email }}
                                    </div>
                                </div>

                                <!-- Submit Button -->
                                <button type="submit" class="btn btn-login" :disabled="form.processing">
                                    <span v-if="form.processing">
                                        <i class="bi bi-arrow-repeat spinning me-2"></i>
                                        Sending...
                                    </span>
                                    <span v-else>
                                        <i class="bi bi-envelope-paper me-2"></i>
                                        Email Password Reset Link
                                    </span>
                                </button>

                                <!-- Back to Login Link -->
                                <div class="signup-link">
                                    <p><a href="/">Back to Login</a></p>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3';
import { useNotifications } from '@/composables/useNotifications';

// Initialize notifications
const { success, error } = useNotifications();

// Form data using Inertia's useForm for better error handling
const form = useForm({
    email: '',
});

// Handle form submission
const handleSubmit = () => {
    form.post('/forgot-password', {
        preserveScroll: true,
        onSuccess: () => {
            success('Password reset link has been sent to your email!');
        },
        onError: () => {
            error('Failed to send password reset link. Please check your email and try again.');
        },
    });
};
</script>

<style scoped>
/* Login Container */
.login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    position: relative;
    overflow: hidden;
    font-family: 'Fira Sans', sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Background Elements */
.background-elements {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
}

.bg-circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
}

.circle-1 {
    width: 300px;
    height: 300px;
    top: -100px;
    left: -100px;
}

.circle-2 {
    width: 400px;
    height: 400px;
    bottom: -150px;
    right: -150px;
}

.circle-3 {
    width: 200px;
    height: 200px;
    top: 50%;
    left: 10%;
    transform: translateY(-50%);
}

/* Login Section */
.login-section {
    position: relative;
    z-index: 2;
}

.login-card {
    background: white;
    border-radius: 24px;
    padding: 3rem;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.login-header {
    text-align: center;
    margin-bottom: 2rem;
}

.brand-logo-center {
    display: flex;
    justify-content: center;
    align-items: center;
}

.logo-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(30, 149, 121, 0.3);
}

.logo-icon i {
    font-size: 2.5rem;
    color: white;
}

.login-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1e9579;
    margin-bottom: 0.5rem;
}

.login-subtitle {
    font-size: 1rem;
    color: #6c757d;
    margin-bottom: 0;
}

/* Form Styles */
.login-form .form-label {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
}

.form-control {
    padding: 0.875rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.form-control:focus {
    border-color: #1e9579;
    box-shadow: 0 0 0 0.2rem rgba(30, 149, 121, 0.15);
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.btn-login {
    width: 100%;
    padding: 0.875rem;
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    border: none;
    border-radius: 12px;
    color: white;
    font-size: 1rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(30, 149, 121, 0.3);
}

.btn-login:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(30, 149, 121, 0.4);
}

.btn-login:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinning {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Sign Up Link */
.signup-link {
    text-align: center;
    margin-top: 1.5rem;
}

.signup-link p {
    color: #6c757d;
    font-size: 0.95rem;
    margin: 0;
}

.signup-link a {
    color: #1e9579;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.3s ease;
}

.signup-link a:hover {
    color: #2dd4bf;
    text-decoration: underline;
}

/* Alert Styles */
.alert {
    border-radius: 12px;
    padding: 1rem;
    border: none;
}

.alert-success {
    background-color: #d4edda;
    color: #155724;
}

/* Responsive Design */
@media (max-width: 768px) {
    .login-card {
        padding: 2rem 1.5rem;
    }

    .login-title {
        font-size: 1.5rem;
    }

    .logo-icon {
        width: 60px;
        height: 60px;
    }

    .logo-icon i {
        font-size: 2rem;
    }
}
</style>
